import React, { useState } from 'react';
import { Wrench, Loader2, Wand, ArrowUpWideNarrow, Zap, AlertTriangle, Users, Route, Network, SquareStack, Target, CheckCircle2, Clock, Flag } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const PROMPTS = [
	{
		key: 'CrossFWImpact',
		label: 'Cross-Framework Impact',
		icon: Network,
		description: 'Shows issues that impact multiple frameworks at once.'
	},
	{
		key: 'QuickFix',
		label: 'Quick Fix',
		icon: Zap,
		description: '"What can be fixed quickly?" Suggests the fastest, lowest-effort fixes.'
	},
	{
		key: 'FixFirst',
		label: 'Fix First',
		icon: ArrowUpWideNarrow,
		description: '"What should we fix first?" Shows the most important fixes to do first.'
	},
	{
		key: 'MostCriticalIssues',
		label: 'Most Critical Issues',
		icon: AlertTriangle,
		description: '"What are the most critical issues to be fixed?" Lists the most important problems to address.'
	},
	{
		key: 'ExplainToLeadership',
		label: 'Explain to Leadership',
		icon: Users,
		description: '"What should I explain to my leadership?" Summarizes issues for executive/leadership audience.'
	},
	{
		key: 'SingleFixMultipleFW',
		label: 'Single Fix, Multiple Frameworks',
		icon: Target,
		description: '"Which fix improves multiple frameworks simultaneously?" Shows fixes that improve compliance in several frameworks.'
	},
	{
		key: 'CascadingImprovement',
		label: 'Cascading Improvement Effects',
		icon: SquareStack,
		description: 'Shows fixes that unlock further improvements.'
	},
	{
		key: 'MinimumEffort80',
		label: 'Minimum Path to 80% Compliance',
		icon: Route,
		description: '"What is the minimum effort path to reach 80% compliance?" Shows a phased plan to reach 80% compliance with minimal effort.'
	},
];

const IssueSolverTab = ({ scanId }) => {
	const [selectedPrompt, setSelectedPrompt] = useState(null);
	const [loading, setLoading] = useState(false);
	const [aiResponse, setAiResponse] = useState(null);
	const [error, setError] = useState(null);
	const [cached, setCached] = useState(false);

	const handlePromptSelect = async (prompt) => {
		setSelectedPrompt(prompt);
		setAiResponse(null);
		setError(null);
		setLoading(true);
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), 5000);
		try {
			const cachedResponse = await fetch(`${ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Issue Solver")}?issueSolverPrompt=${prompt.key}`, { method: 'POST', signal: controller.signal, credentials: 'include' });
			clearTimeout(timeoutId);
			if (cachedResponse.status === 200) {
				setCached(true);
				const json = await cachedResponse.json();
				const data = JSON.parse(json.response);
				setAiResponse({
					promptKey: prompt.key,
					output: data,
					tokensUsed: json.tokens_used,
					costUsd: json.cost_usd,
					lastUpdated: json.lastUpdated,
				});
			} else {
				await handleGenerate(prompt);
			}
		} catch (err) {
			setError('Unable to retrieve AI analysis. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleGenerate = async (prompt) => {
		try {
			setCached(false);
			setAiResponse(null);
			setError(null);
			setLoading(true);
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), 60000);
			const response = await fetch(ApiNames.AI_HUB_ISSUE_SOLVER(scanId, prompt.key), { method: 'POST', signal: controller.signal, credentials: 'include' });
			clearTimeout(timeoutId);
			if (!response.ok) throw new Error('Request failed');
			const json = await response.json();
			const data = JSON.parse(json.response);
			setAiResponse({
				promptKey: prompt.key,
				output: data,
				tokensUsed: json.tokens_used,
				costUsd: json.cost_usd,
				lastUpdated: json.lastUpdated,
			});
		} catch (err) {
			setError('Unable to generate AI analysis. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Render for MinimumEffort80 prompt
	// const renderMinimumEffort80 = (data) => {
	// 	if (!data || typeof data !== 'object') return null;
	// 	const phases = Object.entries(data).filter(([k]) => k.startsWith('Phase'));
	// 	return (
	// 		<div className="flex flex-row space-x-8">
	// 			{phases.map(([phaseKey, phase]) => (
	// 				<div key={phaseKey} className="mb-4">
	// 					<h5 className="font-bold text-base mb-1">{phase.Title}</h5>
	// 					<ul className="list-disc ml-6 text-black-400">
	// 						{phase.Fixes.map((fix, idx) => (
	// 							<li key={idx}>{fix}</li>
	// 						))}
	// 					</ul>
	// 				</div>
	// 			))}
	// 		</div>
	// 	);
	// };
    const renderMinimumEffort80 = (data) => {
        if (!data || typeof data !== 'object') return null;

        // Filter and sort phase keys (Phase 1, Phase 2, etc.)
        const phases = Object.entries(data)
            .filter(([k]) => k.startsWith('Phase'))
            .sort(([a], [b]) => a.localeCompare(b));

        // Configuration to match the specific HTML styles and Lucide icons
        const phaseConfig = [
            {
            textColor: 'text-emerald-600', // Success
            Icon: CheckCircle2,
            bullet: '✓',
            },
            {
            textColor: 'text-amber-500',   // Warning
            Icon: Clock,
            bullet: '⚡',
            },
            {
            textColor: 'text-blue-500',    // Primary Light
            Icon: Flag,
            bullet: '🔧',
            },
        ];

        return (
            <div className="flex flex-wrap gap-4">
            {phases.map(([phaseKey, phase], index) => {
                // Fallback to the last config if there are more than 3 phases
                const config = phaseConfig[index] || phaseConfig[phaseConfig.length - 1];
                const { Icon, textColor, bullet } = config;

                return (
                <div key={phaseKey} className="flex-1 min-w-[280px]">
                    <h5 className={`font-bold text-base mb-3 flex items-center gap-2 ${textColor}`}>
                    <Icon size={18} strokeWidth={2.5} />
                    {phase.Title}
                    </h5>
                    <ul className="space-y-2">
                    {phase.Fixes.map((fix, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700 text-sm">
                        <span className="shrink-0 font-mono">{bullet}</span>
                        <span>{fix}</span>
                        </li>
                    ))}
                    </ul>
                </div>
                );
            })}
            </div>
        );
    };

	return (
		<div className="flex flex-col items-center gap-8">
			<div className="w-full max-w-6xl">
				<div className="card-header flex items-center justify-between">
					<div className="card-title flex items-center gap-2">
						<Wrench className="w-6 h-6 text-blue-400" />
						<h3 className="font-bold text-lg">AI Issue Solver - Strategic Remediation Intelligence</h3>
					</div>
					<span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold"><Wand className="w-4 h-4 mr-1" />AI-Powered</span>
				</div>
				<p className="text-gray-400 mb-6 text-sm mt-2">
					Select a prompt to receive targeted, actionable compliance fixes. The AI Issue Solver analyzes your scan and recommends the most effective next steps for rapid improvement.
				</p>
				<div className="persona-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
					{PROMPTS.map((prompt) => {
						const Icon = prompt.icon;
						return (
							<button
								key={prompt.key}
								className={`persona-card flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 shadow-sm cursor-pointer focus:outline-none h-full ${selectedPrompt?.key === prompt.key ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105' : 'border-base-300 bg-base-100 hover:border-blue-400'}`}
								onClick={() => handlePromptSelect(prompt)}
								disabled={loading}
								type="button"
							>
								<span className="persona-icon mb-2">
									<Icon className="w-8 h-8 text-blue-400" />
								</span>
								<h4 className="font-semibold text-base mb-1 text-center">{prompt.label}</h4>
								<p className="text-xs text-gray-400 text-center mt-1">{prompt.description}</p>
							</button>
						);
					})}
				</div>
				<div className="w-full">
					{loading && (
						<div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
							<Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
							<span className="text-gray-400">Generating AI analysis...</span>
						</div>
					)}
					{!loading && error && (
						<div className="ai-response text-center text-red-400 py-12">{error}</div>
					)}
					{!loading && !error && aiResponse && (
						<div className="ai-response bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn">
							<h4 className="font-bold text-lg mb-2">{PROMPTS.find(p => p.key === aiResponse.promptKey)?.label || 'AI Issue Solver Result'}</h4>
							{aiResponse.promptKey === 'MinimumEffort80' ? (
								renderMinimumEffort80(aiResponse.output)
							) : (
								<p className="mb-4 text-black-400" style={{whiteSpace: 'pre-line'}}>{aiResponse.output.output}</p>
							)}
							<p className="mb-2 text-gray-400">Tokens used: {aiResponse.tokensUsed}. Cost: ${aiResponse.costUsd}.</p>
							{cached && (
								<p className="mb-2 text-gray-400">Viewing cached Issue Solver result. Last updated: {aiResponse.lastUpdated}.</p>
							)}
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
								onClick={() => handleGenerate(selectedPrompt)}
								disabled={loading || !scanId}
							>
								Regenerate Solution
							</button>
						</div>
					)}
					{!loading && !error && !aiResponse && (
						<div className="ai-response text-center text-gray-400 py-12">
							Select a prompt to generate an AI-powered compliance fix or recommendation tailored to your needs.
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default IssueSolverTab;
